import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import LoadingOverlay from "react-loading-overlay";

import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import * as yup from "yup";

import _ from "lodash/fp";

import SectionHeader from "../../../components/SectionHeader/SectionHeader";
import ErrorFooter from "../../../components/ErrorFooter/ErrorFooter";

import InvoiceDetailsForm from "./UI/InvoiceDetails";
import { MuiAutoComplete } from "../../../components/AutoComplete/MuiAutoComplete";

import { constants } from "../../../constants";

import { request, handleErrors } from "../../../services/helpers";

const itemRow = {
  product: yup
    .object()
    .typeError("Select a product or remove this row.")
    .required("Select a product or remove this row."),
  units: yup
    .number()
    .typeError("Units is a required field.")
    .required("Units.required_message"),
  amount: yup.number().required("amount.required_message"),
};

const validationSchema = yup.object().shape({
  membershipName: yup
    .object()
    .required("Membership Name is a required field.")
    .nullable(),
  itemList: yup
    .array()
    .of(yup.object().shape(itemRow))
    .required("Items list must contain atleast 1 item.")
    .min(1, "Minimum of 1 field"),
  invoiceDate: yup
    .date()
    .typeError("Invoice Date is a required field.")
    .required("Invoice Date is a required field."),
  paymentTerms: yup
    .object()
    .typeError("Payment Terms is a required field.")
    .required("Payment Terms is a required field."),
  dueDate: yup
    .date()
    .typeError("Invoice Date is a required field.")
    .required("Invoice Date is a required field."),
  invoiceSubject: yup.string().required("Invoice subject is a required field."),
  customNotes: yup.string().optional(),
  termsAndConditions: yup.mixed().optional(),
  // age: yup.number().positive().integer().required(),
});

const AdhocInvoice = (props) => {
  const [loading, setLoading] = useState(false);
  const [membershipList, setMembershipList] = useState([]);
  const [membership, setMembership] = useState(null);
  const [isInvoiceCreated, setInvoiceStatus] = useState(false);
  const [fetchError, setFetchError] = useState("");

  const history = useHistory();

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      customNotes: "Greetings from 2gethr.",
      termsAndConditions:
        "Please pay your invoice on or before the due date. Thanks",
      subTotal: 0,
      discount: 0,
      totalAmount: 0,
    },
  });
  const {
    control,
    handleSubmit,
    errors,
    setError,
    watch,
    getValues,
    setValue,
  } = methods;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await request.get("/memberships/allMembershipForAdminWeb");
        setMembershipList(res.data);
      } catch (err) {
        const membershipsfetchError = handleErrors(err) || err;
        setFetchError(membershipsfetchError);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  const onSubmit = async (data) => {
    // console.log(data);
    setLoading(true);
    try {
      const res = await request.post("/invoices/generate-manually", data);
      setInvoiceStatus(true);
    } catch (err) {
      const fetchError = handleErrors(err) || err;
      setFetchError(fetchError);
    }
    setLoading(false);

    // if (res) console.log(res);
  };

  // console.log("Errors:", errors);

  //   const formSubmit = (e) => {
  //     e.preventDefault();
  //     props.handleSubmit();
  //   onSubmit={props.handleSubmit}
  //   };

  const getMembershipCB = (data) => {
    setMembership(data);
  };

  return (
    <LoadingOverlay active={loading} spinner={loading} text="Loading...">
      {isInvoiceCreated ? (
        <div className="flex flex-col justify-center h-screen items-center">
          <div>
            <img
              src={
                isInvoiceCreated
                  ? constants.postSpocConfirmation
                  : constants.preSpocConfirmation
              }
              alt="pre-tick"
            />
          </div>
          <div className="mt-10">
            <div className="text-center justify-center">
              <h2 className="font-medium md:text-4xl text-2xl">
                Adhoc Invoice
              </h2>
              <h2 className="font-medium md:text-4xl text-2xl">Generated.</h2>
            </div>
            <div className=" text-center mt-10">
              <h4 className="text-l leading-6">Towards</h4>
              <h3 className="font-medium text-xl leading-6">
                {membership.name}
              </h3>
            </div>
            <button
              onClick={() => history.push("/invoices")}
              className="mt-10 mx-10 bg-themeYellow rounded-md border-gray-500 uppercase px-32 py-4 font-bold text-base leading-4 tracking-wide outline-none"
            >
              View all Invoices
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-formBackground h-full">
          <SectionHeader
            path={["Invoices", "Adhoc Invoice Generation"]}
            title={"Adhoc Invoice Generation"}
          />
          {/* Organisation Form */}
          <FormProvider {...methods}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="pb-24 bg-formBackground"
            >
              <div className="lg:flex lg:px-32 px-8 mt-16">
                <div className="lg:max-w-xs lg:w-1/6 mr-6">
                  <h4 className="font-medium text-2xl leading-7">
                    Customer Selection
                  </h4>
                </div>
                <div className="lg:mt-0 my-8 w-full">
                  {/* Select Membership */}
                  <div className="w-full md:w-1/2 mb-6 lg:pr-6 md:pr-6">
                    <label
                      className="block truncate tracking-wide text-black font-display text-sm leading-5 font-medium mb-2"
                      htmlFor="center-location"
                    >
                      Select a Membership
                    </label>
                    <div className="flex flex-row w-full">
                      <div className="flex-10 w-full">
                        {/* <Select label="Age" ref={register} /> */}

                        <MuiAutoComplete
                          name="membershipName"
                          autoComplete={true}
                          control={control}
                          getOptionLabel={(option) =>
                            option?.name || String(option?.membershipNumber)
                          }
                          renderOption={(option) => (
                            <span>
                              {option?.name || String(option?.membershipNumber)}
                            </span>
                          )}
                          getOptionSelected={(option, name) =>
                            _.isEqual(option, name)
                          }
                          options={membershipList}
                          inputProps={{ placeholder: "Select a Membership" }}
                          customChangeCB={getMembershipCB}
                        />
                        {errors.membershipName && (
                          <div className="text-xs text-red-500 flex flex-row-reverse">
                            <span>{errors.membershipName.message}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <hr className="border border-b-0 border-black mx-24 border-opacity-25" />
              {/* Membership Details Form */}
              {membership && (
                <div>
                  <div className="lg:flex lg:px-32 px-8 lg:py-16 py-8">
                    <div className="lg:mr-6 max-w-xs lg:w-1/6">
                      <h4 className="font-medium text-2xl leading-7">
                        Invoice Details
                      </h4>
                    </div>
                    <div className="lg:mt-0 my-8 w-full">
                      <InvoiceDetailsForm {...props} {...methods} />
                    </div>
                  </div>

                  <div className="shadow-lg flex flex-row-reverse py-4 px-8 border border-b-2 items-center">
                    <button
                      type="submit"
                      className="bg-yellow-500 py-4 px-16 rounded font-bold text-sm tracking-widest leading-4 uppercase outline-none"
                    >
                      Generate
                    </button>
                  </div>
                </div>
              )}
            </form>
          </FormProvider>
          {fetchError && (
            <div className="error-footer">
              <ErrorFooter message={fetchError} />
            </div>
          )}
        </div>
      )}
    </LoadingOverlay>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AdhocInvoice);
